/**
 * Stripe.js singleton for the embedded Payment Element checkout. Loaded once;
 * the publishable key must pair (test↔test / live↔live) with the edge function's
 * STRIPE_CHECKOUT_SECRET.
 *
 * Live vs sandbox is chosen by hostname — mirroring the edge function's
 * origin-based resolveStripeEnv — so a single build can serve both. Only the
 * production hosts use the live key; every other host (staging, Vercel previews,
 * localhost) uses the sandbox test key. Both keys are baked into every build
 * (publishable keys are public), and the right one is picked at runtime.
 */
import { loadStripe, type Stripe } from "@stripe/stripe-js";

// Production hosts get the live publishable key; everything else is sandbox.
const LIVE_HOSTS = [
  "ahead-booking-git-dev-ahead-healths-projects.vercel.app",
  "booking.aheadhealth.com",
  // The membership-first app (apps/membership-first), as in the edge
  // functions' LIVE_ORIGINS.
  "book.aheadhealth.com",
];

const isLiveHost =
  typeof window !== "undefined" &&
  LIVE_HOSTS.includes(window.location.hostname);

const publishableKey = isLiveHost
  ? (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "")
  : (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_SANDBOX ?? "");

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}
